<template>
  <section class="tw:flex tw:max-w-[840px] tw:flex-col tw:gap-7" data-testid="reg-step-package">
    <RegistrationStepHeading :title="$t('registration.package.title')" :lede="$t('registration.package.lede')" />

    <div
      class="tw:grid tw:grid-cols-1 tw:items-start tw:gap-5 tw:sm:grid-cols-2"
      role="radiogroup"
      :aria-label="$t('registration.package.title')">
      <label
        v-for="option in packageOptions"
        :key="option.value"
        class="tw:flex! tw:relative tw:h-full tw:cursor-pointer tw:select-none tw:flex-col tw:gap-4 tw:rounded-lg tw:border tw:border-solid tw:bg-white tw:p-6 tw:text-left tw:transition-[border-color,box-shadow] tw:duration-150 tw:ease-in-out tw:hover:tw:border-primary tw:focus-within:tw:shadow-[0_0_0_4px_rgb(0_122_255/0.1)]"
        :class="
          campaignMode === option.value
            ? 'tw:border-2 tw:border-primary tw:shadow-raised'
            : 'tw:border-gray-300 tw:shadow-[0_1px_2px_0_rgb(16_24_40/0.05)]'
        "
        :data-testid="`reg-package-${option.value}`">
        <input v-model="campaignMode" type="radio" name="reg-package" :value="option.value" class="tw:sr-only" />

        <div class="tw:flex tw:items-start tw:justify-between tw:gap-3">
          <span
            class="tw:flex tw:size-10 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-lg tw:text-15 tw:font-bold"
            :class="option.badgeClass"
            aria-hidden="true">
            <component :is="option.badgeIcon" v-if="option.badgeIcon" :size="20" />
            <template v-else>{{ option.badgeText }}</template>
          </span>
          <span class="tw:flex tw:items-center tw:gap-2">
            <span
              v-if="option.pill"
              class="tw:rounded-full tw:bg-[#f0f7ff] tw:px-2.5 tw:py-1 tw:text-12 tw:font-medium tw:text-primary">
              {{ option.pill }}
            </span>
            <span
              class="tw:flex tw:size-[22px] tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full"
              :class="
                campaignMode === option.value
                  ? 'tw:bg-primary tw:text-white'
                  : 'tw:border tw:border-gray-300 tw:text-transparent'
              "
              aria-hidden="true">
              <IconCheck :size="13" stroke-width="3" />
            </span>
          </span>
        </div>

        <div class="tw:flex tw:flex-col tw:gap-1.5">
          <span class="tw:text-20 tw:font-bold tw:tracking-[-0.01em] tw:text-gray-900">{{ option.title }}</span>
          <span class="tw:text-14 tw:text-muted">{{ option.description }}</span>
        </div>

        <div class="tw:flex tw:flex-col tw:gap-2.5">
          <span v-for="benefit in option.benefits" :key="benefit" class="tw:flex tw:items-start tw:gap-2.5">
            <span
              class="tw:mt-px tw:flex tw:size-[18px] tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:border tw:border-solid tw:border-[#abefc6] tw:bg-[#ecfdf3] tw:text-[#067647]"
              aria-hidden="true">
              <IconCheck :size="11" stroke-width="3" />
            </span>
            <span class="tw:text-14 tw:text-gray-700">{{ benefit }}</span>
          </span>
          <span class="tw:flex tw:items-start tw:gap-2.5">
            <span
              class="tw:mt-px tw:flex tw:size-[18px] tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:border tw:border-solid tw:border-[#fed7aa] tw:bg-[#fff7ed] tw:text-[#c2410c]"
              aria-hidden="true">
              <IconMinus :size="11" stroke-width="3" />
            </span>
            <span class="tw:text-14 tw:text-muted">{{ option.tradeoff }}</span>
          </span>
        </div>

        <div
          class="tw:mt-auto tw:flex tw:items-baseline tw:justify-between tw:gap-3 tw:border-t tw:border-solid tw:border-gray-200 tw:pt-4">
          <span class="tw:text-14 tw:font-semibold tw:text-gray-900">{{ option.price }}</span>
          <span class="tw:text-13 tw:text-muted">{{ option.priceNote }}</span>
        </div>
      </label>
    </div>

    <span class="tw:text-13 tw:text-muted">{{ $t('registration.package.note') }}</span>
  </section>
</template>

<script setup lang="ts">
/**
 * Step 6 - not skippable: the choice sets the `self_service` feature flag. Mirrors `LeadRadioCard`'s
 * native-radio-in-a-label pattern rather than reusing it, as the card layout differs too much.
 */
import { computed, onMounted, watch } from 'vue';
import { IconCommand, IconCheck, IconMinus } from '@tabler/icons-vue';

import { registrationClient } from '@/api/registration';
import RegistrationStepHeading from '@/components/registration/subcomponents/RegistrationStepHeading.vue';
import { useRegistrationStepSync } from '@/composables/useRegistrationStepSync';
import { brand } from '@/config/brands';
import translator from '@/locale/translator';
import { useRegistrationStore } from '@/stores/registration';
import type { CampaignMode } from '@/types/registration';

const PACKAGES: CampaignMode[] = ['managed', 'self'];

/* ----------------------------------- STATE ---------------------------------- */
const allowNext = defineModel<boolean>('allowNext', { required: true });

const store = useRegistrationStore();

/* --------------------------------- COMPUTED --------------------------------- */
const campaignMode = computed({
  get: () => store.draft.package.campaignMode,
  set: (value: CampaignMode | '') => {
    store.draft.package.campaignMode = value;
  },
});

const packageOptions = computed(() =>
  PACKAGES.map((value) => ({
    value,
    title: translator.get(`registration.package.${value}.title`, { product: brand.productName }),
    description: translator.get(`registration.package.${value}.description`),
    price: translator.get(`registration.package.${value}.price`),
    priceNote: translator.get(`registration.package.${value}.price_note`),
    pill: value === 'managed' ? translator.get('registration.package.managed.pill') : '',
    badgeText: value === 'managed' ? brand.productBadge : '',
    badgeIcon: value === 'self' ? IconCommand : undefined,
    badgeClass: value === 'managed' ? 'tw:bg-[#f0f7ff] tw:text-primary' : 'tw:bg-gray-100 tw:text-gray-700',
    benefits: [
      translator.get(`registration.package.${value}.benefit_1`),
      translator.get(`registration.package.${value}.benefit_2`),
      translator.get(`registration.package.${value}.benefit_3`),
    ],
    tradeoff: translator.get(`registration.package.${value}.tradeoff`),
  }))
);

const isFormValid = computed(() => campaignMode.value !== '');

/* --------------------------------- METHODS ---------------------------------- */
async function submit(): Promise<boolean> {
  const response = await registrationClient.submitPackage({ campaignMode: campaignMode.value });
  // carried forward to the completion step, which reads it off the store
  store.setFeatureSetupWarning(response.featureSetupWarning);

  return true;
}

// pre-fills the draft from the backend on every entry; `isFormValid` already watches the draft
useRegistrationStepSync('package');

/* -------------------------------- LIFECYCLE --------------------------------- */
onMounted(() => {
  store.availableSteps.package.onContinue = submit;
  allowNext.value = isFormValid.value;
});

/* --------------------------------- WATCHERS --------------------------------- */
watch(isFormValid, (valid) => {
  allowNext.value = valid;
});
</script>
